import React, { useState, useEffect } from 'react';
import { Loader2, X, Sparkles } from 'lucide-react';
import { WhatsAppOutputs } from './outputs/WhatsAppOutputs';
import { RadioOutputs } from './outputs/RadioOutputs';
import { SocialOutputs } from './outputs/SocialOutputs';
import { VideoOutputs } from './outputs/VideoOutputs';
import { PolicyOutput, MediaOutput } from './outputs/PolicyMediaOutputs';
import { ImpactSignals, EvidenceExtraction } from './outputs/EvidenceImpact';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'radio', label: 'Radio' },
  { key: 'social', label: 'Social' },
  { key: 'video', label: 'Video' },
  { key: 'policy', label: 'Policy' },
  { key: 'media', label: 'Media' },
  { key: 'evidence', label: 'Evidence' },
  { key: 'signals', label: 'Signals' },
];

export const OutputsPreviewModal = ({ open, onClose, story, token }) => {
  const [outputs, setOutputs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [section, setSection] = useState('whatsapp');

  useEffect(() => {
    if (!open || !story?.id) return;
    let aborted = false;
    setOutputs(null);
    setError(null);
    setLoading(true);
    fetch(`${API_URL}/api/admin/stories/${story.id}/preview-outputs`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `Preview failed (${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        if (!aborted) setOutputs(data.generated_outputs || {});
      })
      .catch((e) => {
        if (!aborted) setError(e.message);
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => { aborted = true; };
  }, [open, story?.id, token]);

  if (!open) return null;

  return (
    <div
      data-testid="outputs-preview-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#07743F]">
              <Sparkles className="w-3 h-3" /> Pre-Approval Preview
            </div>
            <h3 className="font-serif text-lg font-bold text-gray-900 mt-1">
              {story?.title || 'Story'}
            </h3>
            <p className="text-xs text-gray-500">
              Outputs are generated for review only. Nothing is saved unless you approve the story.
            </p>
          </div>
          <button
            type="button"
            data-testid="close-preview"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex flex-col items-center gap-3 py-12 text-gray-600">
              <Loader2 className="w-6 h-6 animate-spin text-[#07743F]" />
              <p className="text-sm font-medium">Generating preview…</p>
              <p className="text-xs text-gray-500">
                Takes ~60s. Producing WhatsApp / Radio / Social / Video / Policy / Media / Evidence / Signals.
              </p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded p-3">
              {error}
            </div>
          )}

          {outputs && !loading && (
            <>
              <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-200">
                {SECTIONS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`preview-tab-${s.key}`}
                    onClick={() => setSection(s.key)}
                    className={`px-3 py-2 text-xs font-semibold border-b-2 transition ${
                      section === s.key
                        ? 'border-[#07743F] text-[#07743F]'
                        : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {section === 'whatsapp' && <WhatsAppOutputs data={outputs.whatsapp} />}
              {section === 'radio' && <RadioOutputs data={outputs.radio} />}
              {section === 'social' && <SocialOutputs data={outputs.social} />}
              {section === 'video' && <VideoOutputs data={outputs.video} />}
              {section === 'policy' && <PolicyOutput data={outputs.policy_brief} />}
              {section === 'media' && <MediaOutput data={outputs.media_story} />}
              {section === 'evidence' && <EvidenceExtraction data={outputs.evidence_extraction} />}
              {section === 'signals' && <ImpactSignals data={outputs.impact_signals} />}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            This is a preview. Approval will regenerate and save the final version.
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="dismiss-preview"
            className="px-4 py-1.5 rounded-full bg-[#07743F] text-white text-sm hover:bg-[#065a32]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutputsPreviewModal;
